import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import App from './App';

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  colors: {
    brand: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    primary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      500: '#032D7F',
      600: '#022468',
      700: '#01194b',
    },
    amber: {
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
    },
    slate: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    }
  },
  fonts: {
    heading: `'Tajawal', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
    body: `'Tajawal', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  },
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 2px 4px 0 rgba(0, 0, 0, 0.04)',
    md: '0 4px 12px 0 rgba(0, 0, 0, 0.07)',
    lg: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
    xl: '0 20px 30px -10px rgba(0, 0, 0, 0.12), 0 10px 15px -5px rgba(0, 0, 0, 0.04)',
    glow: '0 0 20px rgba(37, 99, 235, 0.35)',
  },
  radii: {
    none: '0',
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.25rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
  styles: {
    global: {
      body: {
        bg: '#f8fafc',
        color: '#1e293b',
        fontFamily: `'Tajawal', sans-serif`,
        lineHeight: '1.6',
      },
      '#root': {
        minHeight: '100vh',
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '600',
        borderRadius: 'xl',
        transition: 'all 0.2s ease',
        _focusVisible: {
          boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.4)',
        },
      },
      variants: {
        solid: {
          boxShadow: 'sm',
          _hover: {
            transform: 'translateY(-1px)',
            boxShadow: 'md',
          },
          _active: {
            transform: 'translateY(0)',
          },
        },
        gradient: {
          bgGradient: 'linear(to-r, #032D7F, #4f46e5)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(3, 45, 127, 0.3)',
          _hover: {
            bgGradient: 'linear(to-r, #022468, #4338ca)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(3, 45, 127, 0.4)',
          },
          _active: {
            transform: 'translateY(0)',
          },
        },
        glass: {
          bg: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          color: 'slate.800',
          _hover: {
            bg: 'rgba(255, 255, 255, 0.95)',
            transform: 'translateY(-1px)',
            boxShadow: 'sm',
          },
        },
      },
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'white',
          borderRadius: 'xl',
          borderWidth: '1px',
          borderColor: 'slate.200',
          boxShadow: '0 4px 20px -4px rgba(0,0,0,0.05)',
          transition: 'all 0.25s ease',
          _hover: {
            boxShadow: '0 12px 30px -6px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            borderRadius: 'lg',
            borderColor: 'slate.300',
            bg: 'white',
            _hover: {
              borderColor: 'slate.400',
            },
            _focusVisible: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.15)',
            },
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            borderRadius: 'lg',
            borderColor: 'slate.300',
            bg: 'white',
            _hover: {
              borderColor: 'slate.400',
            },
            _focusVisible: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.15)',
            },
          },
        },
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: 'full',
        px: '3',
        py: '1',
        fontWeight: '600',
        textTransform: 'none',
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          borderRadius: '2xl',
          boxShadow: 'xl',
        },
      },
    },
  },
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <ChakraProvider theme={theme}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </ChakraProvider>
);